/**
 * Grace Period Warning Banner
 * Displays urgent warnings when user is in grace period
 */
import React, { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import { useBillingStore, useBillingSelectors, useGracePeriodUrgency, useGracePeriodMessage } from '../../stores/billingStore';

interface GracePeriodBannerProps {
  onUpdatePayment?: () => void;
  dismissible?: boolean;
}

export const GracePeriodBanner: React.FC<GracePeriodBannerProps> = ({
  onUpdatePayment,
  dismissible = true,
}) => {
  const { loadSubscription } = useBillingStore();
  const { isInGracePeriod, graceDaysRemaining } = useBillingSelectors();
  const urgency = useGracePeriodUrgency();
  const message = useGracePeriodMessage();

  const [isDismissed, setIsDismissed] = useState(false);

  // Load subscription on mount
  useEffect(() => {
    loadSubscription();
  }, [loadSubscription]);

  // Reset dismissal when urgency level changes (becomes more urgent)
  useEffect(() => {
    if (urgency === 'critical') {
      setIsDismissed(false); // Never allow dismissing critical warnings
    }
  }, [urgency]);

  if (!isInGracePeriod || !urgency || !message) {
    return null;
  }

  if (isDismissed && urgency !== 'critical') {
    return null;
  }

  const getBannerStyles = () => {
    switch (urgency) {
      case 'critical':
        return {
          container: 'bg-red-600 border-red-700',
          text: 'text-white',
          button: 'bg-white text-red-600 hover:bg-red-50',
          closeButton: 'text-white hover:bg-red-700',
          icon: '🚨',
        };
      case 'urgent':
        return {
          container: 'bg-orange-500 border-orange-600',
          text: 'text-white',
          button: 'bg-white text-orange-600 hover:bg-orange-50',
          closeButton: 'text-white hover:bg-orange-600',
          icon: '⚠️',
        };
      case 'warning':
        return {
          container: 'bg-yellow-400 border-yellow-500',
          text: 'text-gray-900',
          button: 'bg-gray-900 text-white hover:bg-gray-800',
          closeButton: 'text-gray-900 hover:bg-yellow-500',
          icon: '⚠️',
        };
      default:
        return {
          container: 'bg-gray-100 border-gray-200',
          text: 'text-gray-900',
          button: 'bg-gray-900 text-white hover:bg-gray-800',
          closeButton: 'text-gray-600 hover:bg-gray-200',
          icon: 'ℹ️',
        };
    }
  };

  const styles = getBannerStyles();

  // Pulsing animation for critical warnings
  const shouldPulse = urgency === 'critical' && graceDaysRemaining <= 1;

  return (
    <div
      className={`
        ${styles.container}
        ${shouldPulse ? 'animate-pulse' : ''}
        border-b shadow-md
      `}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
        <div className="flex items-center justify-between gap-4">
          {/* Icon and Message */}
          <div className="flex items-center gap-3 flex-1">
            <span className="text-2xl flex-shrink-0">{styles.icon}</span>
            <div className="flex-1">
              <p className={`${styles.text} font-semibold text-sm sm:text-base`}>
                {message}
              </p>
              {urgency === 'critical' && (
                <p className={`${styles.text} text-xs sm:text-sm mt-1 opacity-90`}>
                  Create operations are blocked. View-only access until payment updated.
                </p>
              )}
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex items-center gap-2 flex-shrink-0">
            {onUpdatePayment && (
              <button
                onClick={onUpdatePayment}
                className={`
                  ${styles.button}
                  px-4 py-2 rounded-md font-medium text-sm
                  transition-colors duration-150
                  whitespace-nowrap
                `}
              >
                Update Payment
              </button>
            )}

            {dismissible && urgency !== 'critical' && (
              <button
                onClick={() => setIsDismissed(true)}
                className={`
                  ${styles.closeButton}
                  p-1 rounded-md
                  transition-colors duration-150
                `}
                aria-label="Dismiss warning"
              >
                <X className="w-5 h-5" />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * Grace Period Toast Notification
 * Can be used for inline notifications within pages
 */
export const GracePeriodToast: React.FC<{
  onUpdatePayment?: () => void;
  onDismiss?: () => void;
}> = ({ onUpdatePayment, onDismiss }) => {
  const { isInGracePeriod } = useBillingSelectors();
  const urgency = useGracePeriodUrgency();
  const message = useGracePeriodMessage();

  if (!isInGracePeriod || !urgency || !message) {
    return null;
  }

  return (
    <div
      className={`
        rounded-lg p-4 shadow-lg border
        ${urgency === 'critical' ? 'bg-red-50 border-red-300' :
          urgency === 'urgent' ? 'bg-orange-50 border-orange-300' :
          'bg-yellow-50 border-yellow-300'}
      `}
    >
      <div className="flex items-start gap-3">
        <span className="text-xl flex-shrink-0">
          {urgency === 'critical' ? '🚨' : '⚠️'}
        </span>
        <div className="flex-1">
          <p
            className={`
              font-medium text-sm
              ${urgency === 'critical' ? 'text-red-900' :
                urgency === 'urgent' ? 'text-orange-900' :
                'text-yellow-900'}
            `}
          >
            {message}
          </p>
          {onUpdatePayment && (
            <button
              onClick={onUpdatePayment}
              className={`
                mt-2 text-sm font-semibold underline
                ${urgency === 'critical' ? 'text-red-700 hover:text-red-800' :
                  urgency === 'urgent' ? 'text-orange-700 hover:text-orange-800' :
                  'text-yellow-700 hover:text-yellow-800'}
              `}
            >
              Update Payment Method →
            </button>
          )}
        </div>
        {onDismiss && urgency !== 'critical' && (
          <button
            onClick={onDismiss}
            className="text-gray-500 hover:text-gray-700"
            aria-label="Dismiss"
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>
    </div>
  );
};

export default GracePeriodBanner;
